/**
 * Audit logging helper.
 *
 * Records interactive CRUD operations into the `audit_log` table. Called at the
 * mutation site (which already holds the old in-memory row), so diffs are exact
 * with no extra DB round-trip. Inserts are fire-and-forget: a failure here must
 * never block or revert the user's actual mutation.
 *
 * See `auditActor.ts` for how the acting user is resolved.
 */

import { supabase } from "@/lib/supabase";
import { getAuditActor } from "@/lib/auditActor";
// Diff logic lives in a pure, runtime-agnostic module shared with the
// server-side audit writer (queries.ts) so every surface produces the same
// { field: { old, new } } change shape.
import {
  diffRecords,
  fullRowChanges,
  type AuditChanges,
  type AuditOperation,
  type FieldChange,
} from "../../supabase/functions/capacitor-api/auditDiff";

export { diffRecords, fullRowChanges };
export type { AuditChanges, AuditOperation, FieldChange };

export interface RecordAuditArgs {
  table: string;
  recordId: string;
  operation: AuditOperation;
  changes: AuditChanges;
  recordLabel?: string | null;
  /**
   * Office the audited record belonged to at the time of the action, so the log
   * can honor the global office filter. Pass null when the record has no office
   * (e.g. action_items) — such entries are always shown regardless of filter.
   */
  officeId?: string | null;
  /**
   * How the change was made. Browser/UI writes are always "web" (the default);
   * the REST API ("api") and MCP server ("mcp") record their own source via the
   * server-side writer in queries.ts.
   */
  source?: "web" | "api" | "mcp";
}

/**
 * Fire-and-forget audit insert. Returns synchronously; never throws.
 * No-op UPDATEs (empty changes) are skipped so unchanged saves produce no row.
 */
export function recordAudit(args: RecordAuditArgs): void {
  const { table, recordId, operation, changes, recordLabel, officeId, source } = args;

  if (operation === "UPDATE" && Object.keys(changes).length === 0) {
    return;
  }

  const actor = getAuditActor();

  void supabase
    .from("audit_log")
    .insert({
      table_name: table,
      record_id: recordId,
      operation,
      actor_id: actor.id,
      actor_email: actor.email,
      actor_name: actor.name,
      record_label: recordLabel ?? null,
      changes: changes as never,
      office_id: officeId ?? null,
      source: source ?? "web",
    })
    .then(({ error }) => {
      if (error) {
        console.error("[audit] insert failed", error);
      }
    });
}
